import { useCallback, useEffect, useRef } from 'react';

/**
 * Auto-dismiss timer that pauses while the user hovers or focuses inside the toast
 * (WCAG 2.2.1 / inclusive timing).
 *
 * @param externalPaused — when `true`, the timer is held regardless of internal
 *   state. Used by the viewport to pause all timers while the stack is hovered/focused.
 */
export function useToastAutoDismiss(
  durationMs: number,
  itemId: string,
  onDismissItem: (id: string) => void,
  externalPaused = false
) {
  const remainingMsRef = useRef(durationMs);
  const endTimeRef = useRef(0);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );
  const pausedRef = useRef(false);
  const onDismissRef = useRef(onDismissItem);
  onDismissRef.current = onDismissItem;

  const clearTimer = useCallback(() => {
    if (timeoutRef.current !== undefined) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = undefined;
    }
  }, []);

  const schedule = useCallback(() => {
    clearTimer();
    const ms = remainingMsRef.current;
    if (ms <= 0) {
      if (!pausedRef.current) {
        onDismissRef.current(itemId);
      }
      return;
    }
    endTimeRef.current = Date.now() + ms;
    timeoutRef.current = setTimeout(() => {
      timeoutRef.current = undefined;
      // Re-check pause state at execution time, not scheduling time (WCAG 2.2.1)
      if (!pausedRef.current) {
        onDismissRef.current(itemId);
      }
    }, ms);
  }, [itemId, clearTimer]);

  const pause = useCallback(() => {
    if (durationMs <= 0 || pausedRef.current) {
      return;
    }
    pausedRef.current = true;
    clearTimer();
    remainingMsRef.current = Math.max(0, endTimeRef.current - Date.now());
  }, [durationMs, clearTimer]);

  const resume = useCallback(() => {
    if (durationMs <= 0 || !pausedRef.current) {
      return;
    }
    pausedRef.current = false;
    schedule();
  }, [durationMs, schedule]);

  useEffect(() => {
    if (durationMs <= 0) {
      return undefined;
    }
    pausedRef.current = false;
    remainingMsRef.current = durationMs;
    schedule();
    return clearTimer;
  }, [durationMs, itemId, schedule, clearTimer]);

  useEffect(() => {
    if (durationMs <= 0) return;
    if (externalPaused) {
      pause();
    } else {
      resume();
    }
  }, [externalPaused, durationMs, pause, resume]);

  return { pause, resume };
}
